import QrCode2Icon from "@mui/icons-material/QrCode2";
import classNames from "classnames";
import React, { memo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useHeaderConfig from "../../common/hooks/useHeaderConfig";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { IStallMode } from "../../contexts/ConfigContext/types";
import { PATH } from "../../router/path";
import { addDatadogAction } from "../../services/datadog";
import Condition from "../Conditional/Condition";
import HamburgerDrawer from "../HamburgerDrawer/HamburgerDrawer";
import LanguageSelect from "../LanguageSelect/LanguageSelect";
import LanguageSelectModal from "../LanguageSelectModal/LanguageSelectModal";
import FlintLogo from "../Logo/FlintLogo";
import HWLogo from "../Logo/HWLogo";
import KokoroLogo from "../Logo/KokoroLogo";
import PDDLogo from "../Logo/PDDLogo";
import ShabuDaysLogo from "../Logo/ShabuDaysLogo";
import TorikizokuLogo from "../Logo/TorikizokuLogo";
import NavigationBack from "../NavigationBack/NavigationBack";
import "./LayoutHeader.scss";
import "./LayoutHeader_kokoro.scss";
import "./LayoutHeader_shabuDays.scss";
import "./LayoutHeader_torikizoku.scss";

interface ILayoutHeaderProps {
	"data-testid": string;
	className?: string;
}

const LayoutHeader: React.FC<ILayoutHeaderProps> = (props) => {
	const { t } = useTranslation();
	const [visibleLanguageSelectionModal, setVisibleLanguageSelectionModal] = useState(false);
	const { hideBack, hideLanguageSelect, hideHeader } = useHeaderConfig();
	const { state: mode } = useConfigContext((state) => state.config.stall.mode);
	const { state: devTool } = useConfigContext((state) => state.config.dev_tool);
	const { state: language_select } = useConfigContext((state) => state.config.language_select);
	const { state: table } = useConfigContext((state) => state.table);
	const { state: showOrderHistory } = useConfigContext(
		(state) => state.config.show_order_history ?? true
	);
	const { state: enableCartSharing } = useConfigContext(
		(state) => state.config.enable_cart_sharing ?? false
	);
	const { state: qrSession } = useConfigContext((state) => state.qrSession);
	const location = useLocation();
	const showHamburger =
		!!qrSession?.qrs_id && location.pathname === PATH.PRODUCT_LIST && showOrderHistory;
	const navigate = useNavigate();

	const showShareCartIcon =
		mode === IStallMode.TORIKIZOKU && enableCartSharing && location.pathname === PATH.CART;

	const handleShareCart = useCallback(() => {
		addDatadogAction("header.share_cart_click");
		navigate(PATH.SHARE_CART);
	}, [navigate]);
	const clickCountRef = useRef(0);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const handleHeaderClick = () => {
		if (!devTool && process.env.NODE_ENV === "production") return;

		clickCountRef.current += 1;

		if (clickCountRef.current === 1) {
			timerRef.current = setTimeout(() => {
				clickCountRef.current = 0;
			}, 3000);
		}

		if (clickCountRef.current === 5) {
			clickCountRef.current = 0;
			if (timerRef.current) clearTimeout(timerRef.current);
			navigate(PATH.INFO);
		}
	};

	const handleLanguageSelectClick = () => {
		setVisibleLanguageSelectionModal(true);
	};

	const handleLanguageSelectClose = () => {
		setVisibleLanguageSelectionModal(false);
	};

	if (hideHeader) return null;

	return (
		<div data-testid={props["data-testid"]} className={classNames("LayoutHeader", props.className)}>
			<div className="LayoutHeaderLeft">
				<NavigationBack hideBack={hideBack} />
				<Condition check={showHamburger}>
					<HamburgerDrawer />
				</Condition>
			</div>
			{/* Dev-only 5-click escape hatch to navigate to /info; no end-user interactivity. */}
			<div
				className="LayoutHeaderCenter"
				onClick={handleHeaderClick} // NOSONAR typescript:S6848,typescript:S1082
			>
				<Condition check={mode === IStallMode.FAIRPRICE}>
					<PDDLogo />
				</Condition>
				<Condition check={mode === IStallMode.WANG}>
					<HWLogo />
				</Condition>
				<Condition check={mode === IStallMode.FLINT}>
					<FlintLogo />
				</Condition>
				<Condition check={mode === IStallMode.SHABUDAY}>
					<ShabuDaysLogo />
				</Condition>
				<Condition check={mode === IStallMode.KOKORO}>
					<KokoroLogo />
				</Condition>
				<Condition check={mode === IStallMode.TORIKIZOKU}>
					<TorikizokuLogo />
				</Condition>
			</div>
			<div className="LayoutHeaderRight">
				<Condition check={visibleLanguageSelectionModal}>
					<LanguageSelectModal onClose={handleLanguageSelectClose} />
				</Condition>
				<Condition check={language_select}>
					<LanguageSelect
						onClick={handleLanguageSelectClick}
						hideLanguageSelect={hideLanguageSelect}
					/>
				</Condition>
				<Condition check={!!table && [PATH.PRODUCT_LIST, PATH.CART].includes(location.pathname)}>
					<span className="HeaderTable" data-testid="header-table">
						<span className="HeaderTableLabel">{t("hamburgerDrawer.tableNumber")}</span>
						<span className="HeaderTableValue">{table}</span>
					</span>
				</Condition>
				<Condition check={showShareCartIcon}>
					<button
						className="HeaderShareCartBtn"
						onClick={handleShareCart}
						aria-label="Share cart"
						data-testid="header-share-cart-btn"
					>
						<QrCode2Icon fontSize="small" />
					</button>
				</Condition>
			</div>
		</div>
	);
};

export default memo(LayoutHeader);
